import React, { useEffect, useState } from 'react';
import styles from './EmployerDetailsForm.module.scss';
import { useParams } from 'react-router-dom';
import axiosClient from '../../api/axiosClient';
import Logo from '../../components/logo/Logo';
import TextField from '../../components/textField/TextField';
import TextAreaField from '../../components/textAreaField/TextAreaField';
import Dropdown from '../../components/dropdown/Dropdown';
import CustomDatePicker from '../../components/customDatePicker/CustomDatePicker';
import Button from '../../components/button/Button';
import Loading from '../../components/loading/Loading';
import Success from '../../components/success/Success';
import Error from '../../components/Error/Error';
import LoadingOverlay from '../../components/loadingOverlay/LoadingOverlay';
import { formatPhoneNumber } from '../../utils/stringUtils';
import { useToasts } from '../../contexts/ToastContext';

const EMPLOYMENT_TYPES = [
  { value: 'Full-Time', label: 'Full-Time' },
  { value: 'Part-Time', label: 'Part-Time' },
  { value: 'Contract', label: 'Contract' },
];

const INITIAL_FORM = {
  employerName: '',
  jobTitle: '',
  employmentType: '',
  employmentStartDate: null,
  employerContactName: '',
  employerContactEmail: '',
  employerContactPhone: '',
  additionalNotes: '',
};

const EmployerDetailsForm = () => {
  const { updateToken } = useParams();
  const { showToast } = useToasts();
  const [view, setView] = useState('loading');
  const [student, setStudent] = useState(null);
  const [tokenError, setTokenError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState(INITIAL_FORM);

  useEffect(() => {
    const fetchForm = async () => {
      try {
        const response = await axiosClient.get(`/employer-details/form/${updateToken}`);
        const data = response.data;
        setStudent(data.student);
        if (data.existingDetails) {
          setFormData({
            employerName: data.existingDetails.employerName || '',
            jobTitle: data.existingDetails.jobTitle || '',
            employmentType: data.existingDetails.employmentType || '',
            employmentStartDate: data.existingDetails.employmentStartDate
              ? new Date(data.existingDetails.employmentStartDate) : null,
            employerContactName: data.existingDetails.employerContactName || '',
            employerContactEmail: data.existingDetails.employerContactEmail || '',
            employerContactPhone: data.existingDetails.employerContactPhone
              ? formatPhoneNumber(data.existingDetails.employerContactPhone) : '',
            additionalNotes: data.existingDetails.additionalNotes || '',
          });
        }
        setView('form');
      } catch (error) {
        setTokenError(error.message || 'Invalid or expired link.');
        setView('error');
      }
    };
    fetchForm();
  }, [updateToken]);

  const validate = () => {
    const newErrors = {};
    if (!formData.employerName?.trim()) newErrors.employerName = 'Employer name is required';
    if (!formData.jobTitle?.trim()) newErrors.jobTitle = 'Job title is required';
    if (!formData.employmentType) newErrors.employmentType = 'Employment type is required';
    if (!formData.employmentStartDate) newErrors.employmentStartDate = 'Start date is required';
    if (!formData.employerContactName?.trim()) newErrors.employerContactName = 'Contact name is required';
    if (!formData.employerContactEmail?.trim()) {
      newErrors.employerContactEmail = 'Contact email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.employerContactEmail.trim())) {
      newErrors.employerContactEmail = 'Enter a valid email address';
    }
    if (!formData.employerContactPhone?.trim()) newErrors.employerContactPhone = 'Contact phone is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      await axiosClient.post(`/employer-details/submit/${updateToken}`, {
        employerName: formData.employerName,
        jobTitle: formData.jobTitle,
        employmentType: formData.employmentType,
        employmentStartDate: formData.employmentStartDate.toISOString().split('T')[0],
        employerContactName: formData.employerContactName,
        employerContactEmail: formData.employerContactEmail,
        employerContactPhone: formData.employerContactPhone.replace(/\D/g, ''),
        additionalNotes: formData.additionalNotes,
      });
      setView('success');
    } catch (error) {
      showToast(error.message || 'Failed to submit. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (view === 'loading') return <Loading />;
  if (view === 'success') return <Success message="Your employer details have been submitted successfully." showLogin={false} />;
  if (view === 'error') return <Error message={tokenError} />;

  return (
    <div className={styles.container}>
      {isSubmitting && <LoadingOverlay />}
      <Logo />
      <h1 className={styles.title}>Employer Details</h1>
      {student && (
        <p className={styles.subtitle}>
          Submitting for <strong>{student.firstName} {student.lastName}</strong> — {student.email}
        </p>
      )}
      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        <TextField
          label="Employer Name *"
          value={formData.employerName}
          onChange={(val) => handleChange('employerName', val)}
          placeholder="Enter employer name"
          error={errors.employerName}
        />
        <TextField
          label="Job Title *"
          value={formData.jobTitle}
          onChange={(val) => handleChange('jobTitle', val)}
          placeholder="Enter job title"
          error={errors.jobTitle}
        />
        <Dropdown
          label="Employment Type *"
          value={formData.employmentType}
          onChange={(val) => handleChange('employmentType', val)}
          options={EMPLOYMENT_TYPES}
          error={errors.employmentType}
        />
        <CustomDatePicker
          label="Employment Start Date *"
          date={formData.employmentStartDate}
          onChange={(date) => handleChange('employmentStartDate', date)}
          showIcon
          error={errors.employmentStartDate}
        />
        <TextField
          label="Employer Contact Name *"
          value={formData.employerContactName}
          onChange={(val) => handleChange('employerContactName', val)}
          placeholder="Enter contact name"
          error={errors.employerContactName}
        />
        <TextField
          label="Employer Contact Email *"
          value={formData.employerContactEmail}
          onChange={(val) => handleChange('employerContactEmail', val)}
          placeholder="Enter contact email"
          error={errors.employerContactEmail}
        />
        <TextField
          label="Employer Contact Phone *"
          value={formData.employerContactPhone}
          onChange={(val) => handleChange('employerContactPhone', formatPhoneNumber(val))}
          placeholder="000-000-0000"
          error={errors.employerContactPhone}
        />
        <TextAreaField
          label="Additional Notes"
          value={formData.additionalNotes}
          onChange={(val) => handleChange('additionalNotes', val)}
          placeholder="Any additional information"
        />
        <div className={styles.actions}>
          <Button
            label="Submit"
            variant="primary"
            size="medium"
            type="submit"
          />
        </div>
      </form>
    </div>
  );
};

export default EmployerDetailsForm;
